import React, { useState, useEffect } from 'react';
import { adminGetTierMatrix, adminToggleTierFeature } from '../../services/api';

function TierFeatureMatrix() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    loadMatrix();
  }, []);

  const loadMatrix = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminGetTierMatrix();
      setMatrix(data);
    } catch (err) {
      setError('Failed to load feature matrix. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (tier, feature, currentValue) => {
    const tierId = tier.id;
    const featureId = feature.id;

    // App Access is the master switch: turning it off locks a whole tier out
    if (feature.code === 'app_access' && currentValue) {
      const ok = window.confirm(
        `Switch App Access off for ${tier.display_name}?\n\n` +
        `Everyone in the ${tier.display_name} tier will see only the offline notice, ` +
        `starting with their next request. Admins are not affected.`
      );
      if (!ok) return;
    }

    const key = `${tierId}-${featureId}`;
    setUpdating(prev => ({ ...prev, [key]: true }));

    try {
      await adminToggleTierFeature(tierId, featureId, !currentValue);
      // Update local state
      setMatrix(prev => ({
        ...prev,
        access: {
          ...prev.access,
          [tierId]: {
            ...prev.access[tierId],
            [featureId]: !currentValue
          }
        }
      }));
    } catch (err) {
      setError('Failed to update feature access: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  const getFeatureDescription = (featureCode) => {
    const descriptions = {
      'screener': 'Options screening and discovery tool',
      'watchlist': 'Portfolio tracking and P&L simulation',
      'dashboard_builder': 'Custom dashboards with technical indicators',
      'stock_analyzer': 'Fundamental analysis and fair value',
      'admin_tests': 'Administrative testing dashboard (admin only)',
      'research': 'Research tools for fundamentals, financials, and EPS analysis',
      'scoring_builder': 'Custom weighted scoring methodology builder for ranking stocks',
      'app_access': 'Can open the app at all. No = this tier sees only the offline notice.'
    };
    return descriptions[featureCode] || '';
  };

  if (loading) {
    return (
      <div className="roles-loading">
        <div className="spinner"></div>
        <p>Loading feature matrix...</p>
      </div>
    );
  }

  if (!matrix) {
    return (
      <div className="error-message">
        Failed to load feature matrix.
        <button onClick={loadMatrix} className="retry-btn">Retry</button>
      </div>
    );
  }

  // App Access is the master switch, so it goes first (the API sorts by name,
  // which would put "Admin Tests" above it)
  const orderedFeatures = [...matrix.features].sort(
    (a, b) => (b.code === 'app_access') - (a.code === 'app_access')
  );

  return (
    <div className="tier-feature-matrix">
      <div className="matrix-header">
        <h3>Feature Access Matrix</h3>
        <p className="matrix-subtitle">
          Configure which features are available for each subscription tier.
          Admin users (is_staff=True) bypass all restrictions.
          App Access = No means that tier sees only the offline notice.
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
          <button onClick={() => setError('')} className="dismiss-btn">Dismiss</button>
        </div>
      )}

      <div className="matrix-container">
        <table className="feature-matrix-table">
          <thead>
            <tr>
              <th className="feature-header">Feature</th>
              {matrix.tiers.map(tier => (
                <th key={tier.id} className="tier-header">
                  <span className={`tier-name tier-${tier.name}`}>
                    {tier.display_name}
                  </span>
                </th>
              ))}
              <th className="tier-header admin-header">
                <span className="tier-name tier-admin">Admin</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {orderedFeatures.map(feature => (
              <tr key={feature.id} className={!feature.is_active ? 'disabled-feature' : ''}>
                <td className="feature-cell">
                  <div className="feature-info">
                    <span className="feature-name">{feature.name}</span>
                    <span className="feature-code">{feature.code}</span>
                    <span className="feature-description">
                      {getFeatureDescription(feature.code)}
                    </span>
                  </div>
                  {!feature.is_active && (
                    <span className="feature-disabled-badge">Globally Disabled</span>
                  )}
                </td>
                {matrix.tiers.map(tier => {
                  const key = `${tier.id}-${feature.id}`;
                  const isEnabled = matrix.access[tier.id]?.[feature.id] || false;
                  const isUpdating = updating[key];

                  return (
                    <td key={tier.id} className="access-cell">
                      <label className={`matrix-toggle ${isUpdating ? 'updating' : ''}`}>
                        <input
                          type="checkbox"
                          checked={isEnabled}
                          onChange={() => handleToggle(tier, feature, isEnabled)}
                          disabled={isUpdating || !feature.is_active}
                        />
                        <span className={`toggle-indicator ${isEnabled ? 'enabled' : 'disabled'}`}>
                          {isEnabled ? 'Yes' : 'No'}
                        </span>
                      </label>
                    </td>
                  );
                })}
                <td className="access-cell admin-access">
                  <span className="always-enabled">Always</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="matrix-footer">
        <div className="matrix-legend">
          <span className="legend-item">
            <span className="toggle-indicator enabled">Yes</span> = Feature enabled for tier
          </span>
          <span className="legend-item">
            <span className="toggle-indicator disabled">No</span> = Feature disabled for tier
          </span>
          <span className="legend-item">
            <span className="always-enabled">Always</span> = Admin users bypass all restrictions
          </span>
        </div>
        <button onClick={loadMatrix} className="refresh-btn" disabled={loading}>
          Refresh Matrix
        </button>
      </div>
    </div>
  );
}

export default TierFeatureMatrix;
